import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import { useToast } from '../../../../hooks/useToast';
import { 
  chartOfAccountsService, 
  getAccountTypeOptions, 
  getAccountCategoryOptions,
  REPORT_CLASSIFICATIONS 
} from '../../../../api/accounting';

const ChartOfAccountsTab = () => {
  const { showToast } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterType, setFilterType] = useState('');
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [limit, setLimit] = useState(50);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, [currentPage, searchTerm, filterType, limit]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const result = await chartOfAccountsService.getAll({
        page: currentPage,
        limit: limit,
        search: searchTerm,
        accountType: filterType,
        isActive: true
      });
      
      // Handle new response structure with pagination object
      if (result && result.docs) {
        setAccounts(result.docs);
        // Check if pagination object exists within result
        if (result.pagination) {
          setTotalPages(result.pagination.totalPages || 1);
          setTotalCount(result.pagination.totalDocs || 0);
          setHasPrevPage(result.pagination.hasPrevPage || false);
          setHasNextPage(result.pagination.hasNextPage || false);
        } else {
          // Fallback to direct properties
          setTotalPages(result.totalPages || 1);
          setTotalCount(result.totalDocs || 0);
          setHasPrevPage(currentPage > 1);
          setHasNextPage(currentPage < (result.totalPages || 1));
        }
      } else if (result && Array.isArray(result)) {
        // Handle array response (older structure)
        setAccounts(result);
        setTotalPages(1);
        setTotalCount(result.length);
        setHasPrevPage(false);
        setHasNextPage(false);
      } else {
        // Fallback for any other structure
        setAccounts(result?.records || []);
        setTotalPages(result?.pagination?.total || 1);
        setTotalCount(result?.pagination?.totalCount || 0);
        setHasPrevPage(false);
        setHasNextPage(false);
      }
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
      showToast('Failed to fetch chart of accounts', 'error');
      setAccounts([]);
      setTotalPages(1);
      setTotalCount(0);
      setHasPrevPage(false);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      accountCode: '',
      accountName: '',
      accountType: '',
      accountCategory: '',
      reportClassification: '',
      realPersonalType: 'real',
      realAssetCategory: '',
      parentAccount: null,
      description: '',
      tags: [],
      allowsPosting: true,
      isActive: true,
      openingBalance: 0,
      currentBalance: 0,
      normalBalance: 'debit'
    });
  };

  const handleAdd = () => {
    resetForm();
    setSelectedAccount(null);
    setShowAddDialog(true);
  };

  const handleEdit = (account) => {
    setFormData({
      accountCode: account.accountCode || '',
      accountName: account.accountName || '',
      accountType: account.accountType || '',
      accountCategory: account.accountCategory || '',
      reportClassification: account.reportClassification || '',
      realPersonalType: account.realPersonalType || 'real',
      realAssetCategory: account.realAssetCategory || '',
      parentAccount: account.parentAccount?.id || account.parentAccount?._id || null,
      description: account.description || '',
      tags: account.tags || [],
      allowsPosting: account.allowsPosting !== undefined ? account.allowsPosting : true,
      isActive: account.isActive !== undefined ? account.isActive : true,
      openingBalance: account.openingBalance || 0,
      currentBalance: account.currentBalance || 0,
      normalBalance: account.normalBalance || 'debit'
    });
    setSelectedAccount(account);
    setShowEditDialog(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    
    try {
      if (selectedAccount) {
        const accountId = selectedAccount.id || selectedAccount._id;
        await chartOfAccountsService.update(accountId, formData);
        showToast('Account updated successfully', 'success');
      } else {
        await chartOfAccountsService.create(formData);
        showToast('Account created successfully', 'success');
      }
      
      setShowAddDialog(false);
      setShowEditDialog(false);
      setSelectedAccount(null);
      resetForm();
      fetchAccounts();
    } catch (error) {
      console.error('Error saving account:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save account';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      try {
        await chartOfAccountsService.delete(id);
        showToast('Account deleted successfully', 'success');
        fetchAccounts();
      } catch (error) {
        console.error('Error deleting account:', error);
        const errorMessage = error.response?.data?.message || 'Failed to delete account';
        showToast(errorMessage, 'error');
      }
    }
  };

  const handleInitializeAccounts = async () => {
    if (window.confirm('This will initialize default chart of accounts. Any existing accounts may be affected. Continue?')) {
      setLoading(true);
      try {
        await chartOfAccountsService.initializeDefaultAccounts();
        showToast('Default chart of accounts initialized successfully', 'success');
        fetchAccounts();
      } catch (error) {
        console.error('Error initializing accounts:', error);
        const errorMessage = error.response?.data?.message || 'Failed to initialize default accounts';
        showToast(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const getAccountTypeLabel = (type) => {
    const option = getAccountTypeOptions().find(opt => opt.value === type);
    return option ? option.label : type;
  };

  const getCategoryOptions = () => {
    return getAccountCategoryOptions(formData.accountType);
  };

  const getReportClassificationOptions = () => {
    return Object.entries(REPORT_CLASSIFICATIONS).map(([key, value]) => ({
      value,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }));
  };

  const getRealPersonalTypeOptions = () => {
    return [
      { value: 'real', label: 'Real Account' },
      { value: 'personal', label: 'Personal Account' },
      { value: 'nominal', label: 'Nominal Account' }
    ];
  };

  const getRealAssetCategoryOptions = () => {
    return [
      { value: 'category_a', label: 'Category A' },
      { value: 'category_b', label: 'Category B' },
      { value: 'category_c', label: 'Category C' }
    ];
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Chart of Accounts</h3>
          <p className="text-gray-600">Manage your accounting chart of accounts</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleInitializeAccounts}
            className="inline-flex items-center justify-center px-3 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            <Icon name="Database" size={16} className="mr-2" />
            Initialize Default
          </button>
          <button
            onClick={handleAdd}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Account
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search accounts..."
          />
        </div>
        <div className="min-w-48">
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Account Types</option>
            {getAccountTypeOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-32">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Real/Personal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asset Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Normal Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                    No accounts found
                  </td>
                </tr>
              ) : (
                accounts.map((account) => (
                  <tr key={account.id || account._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      <div className="flex items-center space-x-2">
                        <span>{account.accountCode}</span>
                        {account.isSystemAccount && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800" title="System Account">
                            SYS
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-col">
                        <span>{account.accountName}</span>
                        {account.description && (
                          <span className="text-xs text-gray-500 truncate max-w-48" title={account.description}>
                            {account.description}
                          </span>
                        )}
                        {account.tags && account.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {account.tags.slice(0, 3).map((tag, index) => (
                              <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                {tag}
                              </span>
                            ))}
                            {account.tags.length > 3 && (
                              <span className="text-xs text-gray-400">+{account.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getAccountTypeLabel(account.accountType)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.accountCategory?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {account.realPersonalType?.replace(/\b\w/g, l => l.toUpperCase()) || 'Real'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.realAssetCategory ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {account.realAssetCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{account.currentBalance?.toLocaleString() || '0'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        account.normalBalance === 'debit' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {account.normalBalance?.charAt(0).toUpperCase() + account.normalBalance?.slice(1) || 'Debit'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          account.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {account.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {account.allowsPosting !== undefined && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            account.allowsPosting ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {account.allowsPosting ? 'Posting' : 'No Posting'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEdit(account)}
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-200 p-1"
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        {!account.isSystemAccount && (
                          <button 
                            onClick={() => handleDelete(account.id || account._id)}
                            className="text-red-600 hover:text-red-900 transition-colors duration-200 p-1"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalCount)} of {totalCount} results
          </div>
          <div className="flex justify-center sm:justify-end space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={!hasPrevPage}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={!hasNextPage}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      {(showAddDialog || showEditDialog) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-3xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleFormSubmit}>
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {selectedAccount ? 'Edit Account' : 'Add New Account'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.accountCode || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, accountCode: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 1000"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.accountName || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, accountName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Account name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.accountType || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, accountType: e.target.value, accountCategory: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Type</option>
                      {getAccountTypeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.accountCategory || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, accountCategory: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                      disabled={!formData.accountType}
                    >
                      <option value="">Select Category</option>
                      {getCategoryOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Report Classification <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.reportClassification || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, reportClassification: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Classification</option>
                      {getReportClassificationOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Real/Personal Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.realPersonalType || 'real'}
                      onChange={(e) => setFormData(prev => ({ ...prev, realPersonalType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      {getRealPersonalTypeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Real Asset Category
                    </label>
                    <select
                      value={formData.realAssetCategory || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, realAssetCategory: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Category (Optional)</option>
                      {getRealAssetCategoryOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opening Balance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.openingBalance || 0}
                      onChange={(e) => setFormData(prev => ({ ...prev, openingBalance: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Normal Balance <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.normalBalance || 'debit'}
                      onChange={(e) => setFormData(prev => ({ ...prev, normalBalance: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={formData.description || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Account description"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={(formData.tags || []).join(', ')}
                      onChange={(e) => {
                        const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                        setFormData(prev => ({ ...prev, tags }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter tags separated by commas (e.g., bank, operations)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.allowsPosting !== undefined ? formData.allowsPosting : true}
                        onChange={(e) => setFormData(prev => ({ ...prev, allowsPosting: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Allows Posting</span>
                    </label>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.isActive !== undefined ? formData.isActive : true}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddDialog(false);
                      setShowEditDialog(false);
                      setSelectedAccount(null);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
                  >
                    {submitLoading && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    <span>{selectedAccount ? 'Update' : 'Add'} Account</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccountsTab;